import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { debounceTime, Subscription } from 'rxjs';
import { EoFormPanelComponent } from '../eo-form-panel/eo-form-panel.component';
import { EoFormPanelConfig } from '../eo-form-panel/eo-form-panel.model';
import { EoFormConfig } from './eo-form.config';

export type EoFormDisplayMode = 'default' | 'groupLists';

@Component({
  selector: 'eo-form',
  templateUrl: './eo-form.component.html',
  styleUrl: './eo-form.component.scss',
})
export class EoFormComponent {
  protected render = true;
  protected modeOptions = [
    {
      value: 'default',
      icon: 'pi pi-th-large',
    },
    {
      value: 'groupLists',
      icon: 'pi pi-list-check',
    },
  ];
  protected _displayMode: EoFormDisplayMode = 'default';
  @Input() set displayMode(val: EoFormDisplayMode) {
    this._displayMode = val;
    this.onDisplayModeChange(val);
  }
  get displayMode(): EoFormDisplayMode {
    return this._displayMode;
  }

  @ViewChildren('eoFormPanels') eoFormPanels!: QueryList<EoFormPanelComponent>;

  subscriptions: Subscription[] = [];
  form: FormGroup = this.fB.group({});

  protected formPanelConfigs: EoFormPanelConfig[] = [];
  private _config: EoFormPanelConfig[] | EoFormConfig = [];
  @Input() set config(val: EoFormPanelConfig[] | EoFormConfig) {
    this.render = false;
    this._config = val;
    this.form = this.getForm();
    this.formPanelConfigs =
      ((this.config as EoFormConfig)?.config as EoFormPanelConfig[]) ??
      this.config;

    this.cdr.detectChanges();

    this.groupLists = this.getGroupLists(this.formPanelConfigs);

    if ((this.config as EoFormConfig)?.displayMode != undefined) {
      this.displayMode = (this.config as EoFormConfig).displayMode!;
    }

    if (this.displayMode === 'groupLists') {
      this.formPanelConfigs.forEach((c, i) => (c.visible = i == 0));
    }

    this.render = true;
  }

  get displayModeChangeVisible(): boolean {
    return (this.config as EoFormConfig).displayModeChangeVisible ?? false;
  }

  get config() {
    return this._config;
  }

  getFormConfigs() {
    return (
      this.config instanceof EoFormConfig ? this.config.config : this.config
    ) as EoFormPanelConfig[];
  }

  get valid() {
    return this.form.valid;
  }

  protected groupLists: MenuItem[] = [];
  getGroupLists(configs: EoFormPanelConfig[]): MenuItem[] {
    const grouped: { [key: string]: MenuItem } = {};

    configs.forEach((config, i) => {
      const groupName = config.groupName || '';

      if (!grouped[groupName]) {
        grouped[groupName] = {
          label: groupName,
          items: [],
          expanded: i == 0,
        };
      }

      grouped[groupName].items?.push({
        id: config.name,
        label: config.header || config.name,
      });
    });

    return Object.values(grouped);
  }
  selectedFormPanel?: EoFormPanelConfig = this.formPanelConfigs[0];
  onGroupItemClick(item: MenuItem): void {
    this.formPanelConfigs.forEach((c) => (c.visible = c.name === item.id));
    this.selectedFormPanel = this.formPanelConfigs.find(
      (c) => c.name === item.id
    );
  }

  isFormPanelValid(name: string): boolean {
    const panel = this.form.get(name);
    return panel?.valid ?? false;
  }

  isFormPanelDirty(name: string): boolean {
    const panel = this.form.get(name);
    return panel?.dirty ?? false;
  }

  formPanelHasValues(name: string): boolean {
    const panel = this.form.get(name) as FormGroup;
    return Object.values(panel?.controls).some(
      (c) => c.value != null && c.value != undefined && c.value != ''
    );
  }

  formPanelFieldCount(name: string): number {
    return (
      this.formPanelConfigs.find((c) => c.name === name)?.config.length ?? 0
    );
  }

  @Input() customFormControls: { [key: string]: TemplateRef<any> | null } = {};
  @Input() validate = true;
  @Input() styleClass?: string;
  @Input() submitButtonVisible?: boolean;
  @Input() submitButtonLoading = false;
  @Input() submitButtonLabel = 'Αποθήκευση';
  @Input() submitButtonStyleClass?: string;
  @Input() submitButtonDivStyleClass?: string;
  @Input() icon?: string;
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formChange = new EventEmitter<any>();

  constructor(
    private readonly fB: FormBuilder,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.subscriptions = [this.getOnFormChangeSubscription()];
  }

  onDisplayModeChange(displayMode: EoFormDisplayMode): void {
    switch (displayMode) {
      case 'groupLists':
        ((this.config as EoFormConfig)?.config as EoFormPanelConfig[])?.forEach(
          (pnl, i) => (pnl.visible = i == 0)
        );
        break;
      default:
        ((this.config as EoFormConfig)?.config as EoFormPanelConfig[])?.forEach(
          (pnl) => (pnl.visible = true)
        );
    }
  }

  getOnFormChangeSubscription(): Subscription {
    return this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.formChange.emit(this.getFormData());
    });
  }

  getForm() {
    let fg = EoFormPanelConfig.getFormFromConfig(
      this.getFormConfigs(),
      this.fB
    );
    if (!fg) return this.fB.group({});
    return fg;
  }

  getFormControlComponent(panelName: string, formControlName: string) {
    const panelComponents = this.getFormControlComponents(
      panelName
    ) as QueryList<any>;

    return panelComponents.find((c) => c.config.name === formControlName);
  }

  getFormControlComponents(panelName: string) {
    const p = this.eoFormPanels.find(
      (panel) => panel?.config?.name === panelName
    );

    return p?.getFormControlComponents();
  }

  getFormControl(formGroupName: string, formControlName: string) {
    return this.form.get(formGroupName)?.get(formControlName);
  }

  setValue(formGroupName: string, formControlName: string, value: any): void {
    this.getFormControl(formGroupName, formControlName)?.setValue(value);
  }

  getConfig() {
    if (this.config instanceof EoFormConfig) {
      return this.config.config as EoFormPanelConfig[];
    }

    return this.config as EoFormPanelConfig[];
  }

  setFormControlProp(propName: string, value: any): void {}

  isSubmitButtonVisible(): boolean {
    return (
      this.submitButtonVisible ??
      this.getEoFormConfig()?.submitButtonVisible ??
      true
    );
  }

  getEoFormConfig(): EoFormConfig | undefined {
    return this.config as EoFormConfig;
  }

  getSubmitButtonLabel(): string {
    return this.getEoFormConfig()?.submitButtonLabel ?? this.submitButtonLabel;
  }

  getSubmitButtonStyleClass(): string | undefined {
    return (
      this.getEoFormConfig()?.submitButtonStyleClass ??
      this.submitButtonStyleClass
    );
  }

  getSubmitButtonDivStyleClass(): string | undefined {
    return (
      this.getEoFormConfig()?.submitButtonDivStyleClass ??
      this.submitButtonDivStyleClass
    );
  }

  getIcon(): string | undefined {
    return this.getEoFormConfig()?.icon ?? this.icon;
  }

  isSubmitButtonDisabled(): boolean {
    return this.validate && !this.form.valid;
  }

  isSubmitButtonLoading(): boolean {
    return this.submitButtonLoading;
  }

  getFormData(): any {
    return EoFormPanelConfig.getFormData(this.form);
  }

  getStyleClass(): string {
    return this.getEoFormConfig()?.styleClass ?? this.styleClass ?? '';
  }

  onSubmit() {
    if (this.validate && !this.valid) return;

    let formData = EoFormPanelConfig.getFormData(this.form);
    this.formSubmit.emit(formData);
  }

  onActiveStepChange(index: number) {
    (this.config as EoFormConfig).config.forEach(
      (c) => ((c as EoFormPanelConfig).visible = false)
    );
    ((this.config as EoFormConfig).config[index] as EoFormPanelConfig).visible =
      true;
  }

  isStepperVisible(): boolean {
    return this.displayMode === 'groupLists';
  }
}
